import {
  Form,
  LoginRuleCriteria,
  SelectedRole,
} from 'src/components/login-rules/form/types';
import {
  CreateLoginRuleDto,
  LoginRule,
  LoginRuleRole,
  LoginRuleRoleType,
  LoginRuleUserPool,
  LoginRuleUserPoolType,
  UpdateLoginRuleDto,
} from 'src/graphql/generated/graphql';

export const mapLoginRuleToFormData = (entity?: LoginRule): Form => ({
  id: entity?.id,
  name: entity?.name || '',
  isDeleted: entity?.isDeleted,
  isDisabled: entity?.isDisabled,
  description: entity?.description || undefined,
  acceptCriteria:
    entity?.loginRuleUserPools
      ?.filter((pool) => pool.type === LoginRuleUserPoolType.Include)
      .map(mapUserPoolToCriteria) || [],
  exceptCriteria:
    entity?.loginRuleUserPools
      ?.filter((pool) => pool.type === LoginRuleUserPoolType.Exclude)
      .map(mapUserPoolToCriteria) || [],
  rolesIncluded:
    (entity?.loginRuleRoles
      ?.filter((pool) => pool.type === LoginRuleRoleType.Include)
      .map((role) => {
        return { id: role.roleId, name: role.role?.name };
      }) as SelectedRole[]) || [],
  rolesExcluded:
    (entity?.loginRuleRoles
      ?.filter((pool) => pool.type === LoginRuleRoleType.Exclude)
      .map((role) => {
        return { id: role.roleId, name: role.role?.name };
      }) as SelectedRole[]) || [],
});

const mapCriteriaToUserPool = (
  pool: LoginRuleCriteria,
  type: LoginRuleUserPoolType,
): Partial<LoginRuleUserPool> => {
  return {
    managementRoles: pool.managementRoles,
    companyTypes: pool.companyTypes,
    companyNames: pool.companyNames,
    countries: pool.countries,
    fieldOfficeIds: pool.fieldOffices?.map((value: any) => value.toString()),
    programIds: pool.programs,
    userLevels: pool.programLevels,
    certificateIds: pool.certificates?.map((value: any) => value.toString()),
    userActiveDirectoryTypes: pool.userDirectoryIds,
    type: type,
  } as any;
};

const mapUserPoolToCriteria = (
  pool: Partial<LoginRuleUserPool>,
): Partial<LoginRuleCriteria> => {
  return {
    managementRoles: pool.managementRoles,
    companyTypes: pool.companyTypes,
    companyNames: pool.companyNames,
    countries: pool.countries,
    fieldOffices: pool.fieldOffices?.map((item) => item.id),
    programs: pool.programIds,
    programLevels: pool.userLevels,
    certificates: (pool.certificates || [])?.map((item) => item.id),
    userDirectoryIds: pool.userActiveDirectoryTypes,
  };
};

const mapSelectedRoleToLoginRuleRole = (
  role: SelectedRole,
  type: LoginRuleRoleType,
  loginRuleId: string,
): Partial<LoginRuleRole> => {
  return {
    roleId: role.id,
    type: type,
    loginRuleId: loginRuleId,
  };
};

export const mapFormDataToLoginRuleDto = ({
  id,
  form,
  acceptCriteria,
  exceptCriteria,
  rolesIncluded,
  rolesExcluded,
}: {
  id?: string;
  form: Form;
  acceptCriteria: LoginRuleCriteria[];
  exceptCriteria: LoginRuleCriteria[];
  rolesIncluded: SelectedRole[];
  rolesExcluded: SelectedRole[];
}): CreateLoginRuleDto | UpdateLoginRuleDto => {
  return {
    id: id as unknown as string,
    name: form.name,
    description: form.description,
    loginRuleUserPools: acceptCriteria
      .map((pool) => mapCriteriaToUserPool(pool, LoginRuleUserPoolType.Include))
      .concat(
        exceptCriteria.map((pool) =>
          mapCriteriaToUserPool(pool, LoginRuleUserPoolType.Exclude),
        ),
      ) as Partial<LoginRuleUserPool>[],
    loginRuleRoles: rolesIncluded
      .map((role) =>
        mapSelectedRoleToLoginRuleRole(
          role,
          LoginRuleRoleType.Include,
          id as string,
        ),
      )
      .concat(
        rolesExcluded.map((role) =>
          mapSelectedRoleToLoginRuleRole(
            role,
            LoginRuleRoleType.Exclude,
            id as string,
          ),
        ),
      ) as Partial<LoginRuleRole>[],
  };
};
